import { EXPERIENCE_IDS } from '@/components/admin/experiences/contracts';
import type { ReactNode } from 'react';

/**
 * What a box shows while its dry run is being READ AGAIN in the background -
 * a return to this tab (`refetchOnWindowFocus`) - over an answer it already
 * drew (second review of !474).
 *
 * The previous answer STAYS, greyed and said « Vérification en cours… », and
 * the box keeps blocking what it blocked: a refusal that vanished for the
 * length of a request would re-arm a button whose click the server then
 * refuses in 409. On a FIRST reading there is nothing to frame - no previous
 * answer to keep, and a box does not claim before it knows (the head of
 * `experience-editor.tsx`). The save box of the shell, its « Passer en
 * production » and the save box of the page editor all go through here, so
 * the three say it the same way.
 *
 * The answer sits at the SAME place in both states - the note is inserted
 * before it, the grey is a class - so a re-read that starts or ends never
 * remounts it: a link under the pointer or the keyboard stays the same link.
 */

export interface DryRunFrameProps {
  /** A background re-read is under way, over an answer already drawn. */
  readonly rechecking: boolean;
  readonly children: ReactNode;
}

export function DryRunFrame({ rechecking, children }: DryRunFrameProps) {
  return (
    <div aria-busy={rechecking || undefined} className="space-y-2">
      {rechecking ? (
        <output
          data-testid={EXPERIENCE_IDS.dryRunRechecking}
          className="block text-gray-600 text-xs"
        >
          Vérification en cours…
        </output>
      ) : null}
      <div className={rechecking ? 'opacity-60' : undefined}>{children}</div>
    </div>
  );
}
